import NumberInput from "Dashboard/components/NumberInput";
import { ICarnetPayrollUpdate } from "Dashboard/scenes/Carnets/domain/ICarnetPayrollUpdate";
import { useAppDispatch } from "hooks/hooks";
import { debounce } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { monthNames } from "utils/translationDictionary";
import cl from "../../../styles/PayrollPopover.module.scss";
import {
  addExpenseToPayroll,
  getEmployeePayrolls,
  selectEmployee,
  selectState,
  updateCarnetPayrollTemporarily,
  updateEmployeePayroll,
  updateExpenseInCarnetPayrollTemporarily,
  updateExpenseInPayroll
} from "../main.slice";
import { ICarnetPayrollAddExpense } from "Dashboard/scenes/Carnets/domain/ICarnetPayrollAddExpense";
import { ICarnetPayrollUpdateExpense } from "Dashboard/scenes/Carnets/domain/ICarnetPayrollUpdateExpense";
import InfoTooltip from "components/Popup/InfoTooltip";

const PayrollPopover = () => {
  const dispatch = useAppDispatch();
  const employee = useSelector(selectEmployee);
  const { selectedEmployeePayrolls } = useSelector(selectState);
  const [newExpenseAmount, setNewExpenseAmount] = useState<number | undefined>(undefined);
  const [newExpensePayrollId, setNewExpensePayrollId] = useState<number | undefined>(undefined);

  useEffect(() => {
    dispatch(getEmployeePayrolls(employee.employeeId));
  }, [dispatch, employee, employee.employeeId]);

  const onChangeBasicField = (data: ICarnetPayrollUpdate) => {
    dispatch(updateCarnetPayrollTemporarily(data));
    updateBasicFieldWithDelay(data);
  }

  const updateBasicFieldWithDelay = useMemo(
    () => debounce(async (data: ICarnetPayrollUpdate) => {
      try {
        await dispatch(updateEmployeePayroll(data)).unwrap();
        toast.success("Izmena je uspešno zabeležena.");
      } catch (status: any) {
        if (status === 403) {
          toast.error("Neočekivana greška");
        } else if (status === 400) {
          toast.error("Neočekivana greška");
        }
      }
    }, 1000),
    [dispatch]
  );

  const onAddNewExpense = (data: ICarnetPayrollAddExpense) => {
    addExpenseTemporarily(data);
    addExpenseWithDelay(data);
  };

  const addExpenseTemporarily = (data: ICarnetPayrollAddExpense) => {
    setNewExpenseAmount(data.amount);
    setNewExpensePayrollId(data.payrollId);
  };

  const addExpenseWithDelay = useMemo(
    () => debounce(async (data: ICarnetPayrollAddExpense) => {
      setNewExpenseAmount(undefined);
      setNewExpensePayrollId(undefined);
      if (data.amount !== undefined) {
        dispatch(addExpenseToPayroll(data));
      }
    }, 1000),
    [dispatch]
  );

  const onChangeExpenseField = (data: ICarnetPayrollUpdateExpense) => {
    dispatch(updateExpenseInCarnetPayrollTemporarily(data));
    updateExpenseFieldWithDelay(data);
  }

  const updateExpenseFieldWithDelay = useMemo(
    () => debounce(async (data: ICarnetPayrollUpdateExpense) => {
      try {
        await dispatch(updateExpenseInPayroll(data)).unwrap();
        toast.success("Izmena je uspešno zabeležena.");
      } catch (status: any) {
        if (status === 403) {
          toast.error("Neočekivana greška");
        } else if (status === 400) {
          toast.error("Neočekivana greška");
        }
      }
    }, 1000),
    [dispatch]
  );

  return (
    <div className={cl.payrollPopover}>
      <div className={`${cl.grid} ${cl.header}`}>
        <div className={`${cl.row}`}>
          <div className={`${cl.col} ${cl.separatorLine} ${cl.center}`}>datum</div>
          <div className={`${cl.col} ${cl.separatorLine} ${cl.center}`}>na račun</div>
          <div className={`${cl.col} ${cl.separatorLine} ${cl.center}`}>prevoz</div>
        </div>
      </div>
      <div className={`${cl.grid} ${cl.content}`}>
        {selectedEmployeePayrolls !== undefined && selectedEmployeePayrolls.payrolls.map((payroll, index) =>
          <div className={`${cl.row}`} key={payroll.payrollId}>
            <div className={`${cl.col} ${cl.month}`}>
              <span className={`${cl.bgItem} ${cl.borderLight} ${cl.center} ${cl.italic} ${cl.uppercase}`}>
                {monthNames[payroll.month - 1]}
              </span>
            </div>
            <div className={`${cl.col} ${cl.paidByBank}`}>
              <NumberInput
                formatAs="quantity"
                value={`${payroll.paidByBank}`}
                onChange={(value: string, unmaskedValue: string, parsedValue: number) => {
                  if (parsedValue !== payroll.paidByBank) {
                    onChangeBasicField({ payrollId: payroll.payrollId, employeeId: payroll.employeeId, paidByBank: parsedValue });
                  }
                }}
                className={`${cl.bgItem} ${cl.center}`}
              />
            </div>
            <div className={`${cl.col} ${cl.travelExpenses}`}>
              <NumberInput
                formatAs="quantity"
                value={`${payroll.travelExpenses}`}
                onChange={(value: string, unmaskedValue: string, parsedValue: number) => {
                  if (parsedValue !== payroll.travelExpenses) {
                    onChangeBasicField({ payrollId: payroll.payrollId, employeeId: payroll.employeeId, travelExpenses: parsedValue });
                  }
                }}
                className={`${cl.bgItem} ${cl.center}`}
              />
            </div>

            <InfoTooltip tooltip="troškovi" classesProp={`${cl.col} ${cl.expenses}`}>
              {payroll.expenses.map((expense, expenseIndex) =>
                <div className={`${cl.expenseRow}`} key={expense.expenseId}>
                  <NumberInput
                    formatAs="quantity"
                    value={`${expense.amount}`}
                    onChange={(value: string, unmaskedValue: string, parsedValue: number) => {
                      if (parsedValue !== expense.amount) {
                        onChangeExpenseField({ payrollId: payroll.payrollId, employeeId: payroll.employeeId, expenseId: expense.expenseId, amount: parsedValue });
                      }
                    }}
                    className={`${cl.bgItem} ${cl.center} ${cl.expenseAmount}`}
                  />
                  <input className={`${cl.expenseNote}`} value={expense.note}
                    onChange={(event) => onChangeExpenseField({ payrollId: payroll.payrollId, employeeId: payroll.employeeId, expenseId: expense.expenseId, note: event.target.value })}
                  />
                </div>
              )}
              <div className={`${cl.expenseRow}`} key={payroll.payrollId}>
                <NumberInput
                  formatAs="quantity"
                  value={payroll.payrollId === newExpensePayrollId ? `${newExpenseAmount}` : ''}
                  placeholder="dodaj trošak"
                  onChange={(value: string, unmaskedValue: string, parsedValue: number) => {
                    onAddNewExpense({ payrollId: payroll.payrollId, employeeId: payroll.employeeId, amount: parsedValue });
                  }}
                  className={`${cl.bgItem} ${cl.center} ${cl.expenseAmount}`}
                />
              </div>
            </InfoTooltip>

            <div className={`${cl.col} ${cl.empty}`}>
            </div>

            <div className={`${cl.col} ${cl.note}`}>
              <textarea value={payroll.note}
                onChange={(event) => onChangeBasicField({ payrollId: payroll.payrollId, employeeId: payroll.employeeId, note: event.target.value })}
              />
            </div>

          </div>
        )}

      </div>
    </div>
  );
};

export default PayrollPopover;
